import { useCallback, useEffect, useRef, useState } from 'react';
import {
    APP_VERSION,
    BUILD_COMMIT,
    DeployedVersion,
    fetchDeployedVersion,
} from '../helpers/version';

const POLL_INTERVAL_MS = 1 * 60 * 1000

const isEnabled = process.env.NODE_ENV === 'production' && Boolean(BUILD_COMMIT)

const previewInDev =
  process.env.NODE_ENV !== 'production' &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('previewUpdateToast')

type UseVersionCheck = {
  updateInfo: DeployedVersion | null
  dismiss: () => void
}

const useVersionCheck = (): UseVersionCheck => {
  const [updateInfo, setUpdateInfo] = useState<DeployedVersion | null>(null)
  const dismissedCommitRef = useRef<string | null>(null)

  const check = useCallback(async () => {
    const deployed = await fetchDeployedVersion()
    if (!deployed) return
    if (deployed.commit === BUILD_COMMIT) {
      setUpdateInfo(null)
      return
    }
    if (deployed.commit === dismissedCommitRef.current) return
    setUpdateInfo(deployed)
  }, [])

  useEffect(() => {
    if (previewInDev) {
      setUpdateInfo({ version: APP_VERSION || 'preview', commit: 'preview' })
      return undefined
    }

    if (!isEnabled) return undefined

    check()

    const intervalId = window.setInterval(check, POLL_INTERVAL_MS)
    const onFocus = () => check()
    window.addEventListener('focus', onFocus)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener('focus', onFocus)
    }
  }, [check])

  const dismiss = useCallback(() => {
    setUpdateInfo((current) => {
      if (current) dismissedCommitRef.current = current.commit
      return null
    })
  }, [])

  return { updateInfo, dismiss }
}

export default useVersionCheck
